import React, {ForwardedRef, useImperativeHandle, useRef} from 'react';
import {View, StyleSheet, StyleProp, TextStyle} from 'react-native';
import {Colors, HelperInvalid, InputText, LabelMedium, Sizes, TextStyles} from '../index';
import {InputPill} from './InputPill';
import {Platform} from 'react-native';
import {CountryCode} from 'react-native-country-picker-modal';
// @ts-ignore
import {PhoneInput} from './PhoneInput';
import {ComponentProps} from './ComponentProps';

interface FieldsetTextProps extends FieldsetProps {
  inputTextChanged: (text: string) => void;
  inputText?: string | null;
  inputPlaceholder?: string;
  inputAutoFocus?: boolean;
  inputCapitalize?: 'none' | 'words' | 'characters' | 'sentences' | undefined;
  inputAutoCorrect?: boolean;
  inputIsEnabled?: boolean;
}
export const FieldsetText = (props: FieldsetTextProps) => {
  const inputComponent = (
    <InputText
      onTextChanged={props.inputTextChanged}
      text={props.inputText}
      placeholder={props.inputPlaceholder}
      autoFocus={props.inputAutoFocus}
      capitalize={props.inputCapitalize}
      autoCorrect={props.inputAutoCorrect}
      isValid={props.helperInvalidText === null}
      isEnabled={props.inputIsEnabled}
      testID={props.testID + '.InputText'}
    />
  );
  return <Fieldset {...{...props, inputComponent: inputComponent}} />;
};

export interface FieldsetPhoneNumberProps extends FieldsetProps {
  ref: React.RefObject<IFieldsetPhoneNumber>;
  onChangeText: (phoneNumber: string) => void;
  placeholder?: string;
  defaultCountryCode?: CountryCode;
}

export interface IFieldsetPhoneNumber {
  verify(number: string): boolean;
}

export const FieldsetPhoneNumber = React.forwardRef(
  (props: FieldsetPhoneNumberProps, ref: ForwardedRef<IFieldsetPhoneNumber>) => {
    const phoneInputRef = useRef<PhoneInput>(null);
    useImperativeHandle(ref, () => ({
      verify(number: string): boolean {
        return phoneInputRef.current?.isValidNumber(number) ?? false;
      },
    }));
    let textInputPaddingTop;
    let textInputHeight;
    if (Platform.OS === 'ios') {
      textInputPaddingTop = 2;
      textInputHeight = 24;
    } else {
      textInputPaddingTop = 2;
      textInputHeight = 48;
    }
    const textInputStyle: StyleProp<TextStyle> = [
      TextStyles.textMediumM,
      {
        color: Colors.neutralDarkAlpha,
        marginTop: textInputPaddingTop,
        height: textInputHeight,
        marginLeft: -Sizes.s,
      },
    ];
    const textCodeStyle: StyleProp<TextStyle> = [
      TextStyles.textMediumM,
      {
        color: Colors.neutralDarker,
        marginTop: 4,
        marginLeft: -Sizes.l,
        height: 24,
      },
    ];
    // @ts-ignore
    const inputComponent = (
      <InputPill
        borderColor={Colors.neutralLightAlpha}
        isValid={props.helperInvalidText === null}
        paddingHorizontal={0}>
        <View style={phoneNumberStyle.inputPillStyle}>
          <PhoneInput
            placeholder={props.placeholder}
            ref={phoneInputRef}
            defaultCode={props.defaultCountryCode ?? 'BE'}
            // @ts-ignore
            onChangeFormattedText={text => {
              props.onChangeText(text);
            }}
            textContainerStyle={phoneNumberStyle.textContainerStyle}
            textInputStyle={textInputStyle}
            codeTextStyle={textCodeStyle}
            containerStyle={phoneNumberStyle.containerStyle}
            renderDropdownImage={undefined}
          />
        </View>
      </InputPill>
    );
    return <Fieldset {...{...props, inputComponent: inputComponent}} />;
  },
);

const phoneNumberStyle = StyleSheet.create({
  containerStyle: {
    height: 48,
    borderRadius: 25,
    width: 'auto',
  },
  inputPillStyle: {
    flex: 1,
  },
  textContainerStyle: {
    backgroundColor: 'transparent',
  },
});

interface FieldsetProps extends ComponentProps {
  title: string;
  titleRequired?: boolean;
  helperInvalidText: string | null;
}

interface FieldsetInputProps extends FieldsetProps {
  inputComponent: JSX.Element;
}

export const Fieldset = (props: FieldsetInputProps) => {
  return (
    <View style={props.containerStyle}>
      <View style={styles.container} testID={props.testID}>
        <LabelMedium text={props.title} isRequired={props.titleRequired ?? false} />
        {props.inputComponent}
        {props.helperInvalidText && <HelperInvalid text={props.helperInvalidText} />}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1},
});
